import React, {useEffect, useState} from 'react';
import api from "../api"; // Assuming this path is correct
import CompanyStatus from "./User/CompanyStatus"; // Assuming this path is correct
import {useUser} from '../context/UserContext';
import {useNotification} from '../context/NotificationContext';
import RequestConsultationModal from "./popups/RequestConsultationModal"; // Added import for the modal

const LawCompanyWithImage = ({company, onShowPopup, styleClass, showRequestHelpButton = false, onRequestHelp}) => {
    const {authTokens, isAuthenticated} = useUser();
    const {showNotification} = useNotification();
    const [isFavorite, setIsFavorite] = useState(false);
    const [showRequestConsultationModal, setShowRequestConsultationModal] = useState(false); // State for the modal

    useEffect(() => {
        const checkFavoriteStatus = async () => {
            if (!company || !company.id) {
                // console.warn('Company data or ID not available for checking favorites.');
                return;
            }
            try {
                const token = authTokens ? authTokens.access : null;
                if (!token) {
                    // console.warn('No auth token available for checking favorites.');
                    return;
                }
                const config = {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                    }
                };
                const response = await api.get(`/check-favorite-lawcompany/${company.id}/`, config);
                setIsFavorite(response.data.is_favorite);
            } catch (error) {
                console.error('Error checking favorite status:', error);
            }
        };

        if (company && company.id) {
            checkFavoriteStatus();
        }
    }, [company?.id, authTokens]);

    const ensureAuthenticatedForFavorites = () => {
        if (!isAuthenticated || !authTokens?.access) {
            showNotification("Login Required", "Please log in to manage favorites.", null, "warning");
            return false;
        }
        return true;
    };

    const handleAddToFavorites = async () => {
        if (!ensureAuthenticatedForFavorites()) return;
        if (!company || !company.id) return;
        try {
            const response = await api.post('/add-favorite-lawcompany/', {company_id: company.id});
            if (response.status === 200 || response.status === 201) {
                setIsFavorite(true);
            }
        } catch (error) {
            console.error('Error adding to favorites:', error);
        }
    };

    const handleRemoveFromFavorites = async () => {
        if (!ensureAuthenticatedForFavorites()) return;
        if (!company || !company.id) return;
        try {
            const response = await api.post('/remove-favorite-lawcompany/', {company_id: company.id});
            if (response.status === 200) {
                setIsFavorite(false);
            }
        } catch (error) {
            console.error('Error removing from favorites:', error);
        }
    };

    const handleToggleFavorites = () => {
        if (!company || !company.id) return;
        if (isFavorite) {
            handleRemoveFromFavorites();
        } else {
            handleAddToFavorites();
        }
    };

    const truncateDescription = (desc, length) => {
        if (desc && desc.length > length) {
            return desc.substring(0, length) + '...';
        }
        return desc;
    };

    const baseUrl = process.env.REACT_APP_BASE_URL || '';

    const getLogoUrl = (logo) => {
        if (logo && !logo.startsWith('http://') && !logo.startsWith('https://')) {
            return `${baseUrl}${logo}`;
        }
        return logo;
    };

    const formatPhoneNumber = (phoneNumber) => {
        if (!phoneNumber) return '';
        let cleaned = ('' + phoneNumber).replace(/\D/g, '');
        // Original US-centric formatting attempt
        if (cleaned.length === 10 && !cleaned.startsWith('1')) {
            cleaned = '1' + cleaned;
        } else if (cleaned.length > 10 && !cleaned.startsWith('1') && cleaned.length % 10 !== 0 ) {
            // Heuristic for numbers that might be missing a '1' but are longer than 10 digits due to other chars
            // This part of original logic: if (!cleaned.startsWith('1')) { cleaned = '1' + cleaned; }
            // can be problematic if the number is already an international number.
            // For simplicity, matching the original intent for likely US numbers:
        }


        const match = cleaned.match(/^(\d{1})(\d{3})(\d{3})(\d{4})$/); // Assumes a 11-digit number starting with 1 for US
        if (match) {
            return `+${match[1]} (${match[2]}) ${match[3]}-${match[4]}`;
        }
        return phoneNumber; // Return original if formatting fails or not a clear US pattern
    };

    const renderFreeConsultation = () => {
        if (company && company.free_consultation) {
            return (
                <span className="vendor__day">FREE CONSULTATION</span>
            );
        }
        return null;
    };

    const formattedAddress = company?.address
        ? `${company.address.street_address || ''}, ${company.address.city?.name || ''}, ${company.address.state?.name || ''}`.replace(/, ,|^, | ,$/g, '').trim() || 'Address not available'
        : 'Address not available';

    const googleMapsUrl = `https://maps.google.com/?q=${encodeURIComponent(formattedAddress)}`;
    const appleMapsUrl = `http://maps.apple.com/?q=${encodeURIComponent(formattedAddress)}`;

    if (!company) {
        return <div>Company data not available.</div>;
    }

    return (
        <>
            <div className={`vendor ${styleClass}`} style={{
                background: styleClass === 'vendor-day' && company.background_image
                    ? `linear-gradient(rgba(44, 65, 87, 0.7), rgba(44, 65, 87, 0.7)), url(${getLogoUrl(company.background_image)})`
                    : ``
            }}>
                <div className="container">
                    <div className="justify">
                        {styleClass === 'vendor-day' && company.logo && (
                            <div className="vendor__logo js--show-popup" onClick={() => onShowPopup(company.id, 'company')}>
                                <picture>
                                    <source type="image/webp"
                                            srcSet={company.logo ? `${getLogoUrl(company.logo)} 1x, ${getLogoUrl(company.logo)} 2x` : undefined}/>
                                    <source type="image/jpg"
                                            srcSet={company.logo ? `${getLogoUrl(company.logo)} 1x, ${getLogoUrl(company.logo)} 2x` : undefined}/>
                                    <img srcSet={company.logo ? `${getLogoUrl(company.logo)} 1x, ${getLogoUrl(company.logo)} 2x` : undefined}
                                         alt={company.name || 'Company Logo'}
                                         src={getLogoUrl(company.logo)}
                                         />
                                </picture>
                            </div>
                        )}
                        <div className="vendor__data js--show-popup" onClick={() => onShowPopup(company.id, 'company')}>
                            <div className="vendor__header">
                                <h4 className="vendor__name">{company.name || 'Company Name Not Available'}</h4>
                                <button
                                    className={`favorites ${isFavorite ? 'added' : ''}`}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleToggleFavorites();
                                    }}
                                    aria-label={isFavorite ? "Remove from Favorites" : "Add to Favorites"}
                                >
                                    <svg>
                                        <path fillRule="evenodd" clipRule="evenodd"
                                              d="M1.58733 3.10072C3.70393 0.940385 7.13582 0.940385 9.25246 3.10072L10.0323 3.89665L10.7475 3.16683C12.8641 1.0065 16.296 1.0065 18.4127 3.16683C20.5293 5.32717 20.5292 8.82998 18.4127 10.9904L10.4695 19.0901C10.4418 19.1184 10.5003 19.068 10.4695 19.0901C10.2068 19.2849 9.79324 19.2991 9.5566 19.0576L1.58757 10.9239C-0.52903 8.76353 -0.529268 5.26111 1.58733 3.10072Z"></path>
                                    </svg>
                                </button>
                                {styleClass === 'vendor-day' && renderFreeConsultation()}
                                <CompanyStatus status={company.status}/>
                            </div>
                            <span className="vendor__attorney">
                                {company.attorneys ? company.attorneys.length : 0} Attorneys On Staff
                                {company.foundation_year && (
                                    <span> • Founded In {company.foundation_year}</span>
                                )}
                            </span>
                            <p className="vendor__description">{truncateDescription(company.description, 150)}</p>
                        </div>
                        <div className="vendor__contacts">
                            <div className="row">
                                <a className="vendor__link" href={googleMapsUrl} target="_blank"
                                   rel="noopener noreferrer">{formattedAddress}</a>
                                <span className="vendor__label">Address</span>
                            </div>

                            {/* Conditional Phone Display or Request Consultation Button */}
                            {showRequestHelpButton ? (
                                <div className="row">
                                    <button
                                        className="button button-black vendor__button_request_consultation"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (typeof onRequestHelp === 'function') {
                                                onRequestHelp();
                                            } else if (typeof onShowPopup === 'function') {
                                                onShowPopup(company.id, 'company', 'edit-profile');
                                            }
                                        }}
                                        type="button"
                                        disabled={!company}
                                    >
                                        Edit Profile
                                    </button>
                                </div>
                            ) : (
                                styleClass === 'vendor-day' ? (
                                    <div className="row">
                                        <a className="vendor__link"
                                           href={company.phone_number ? `tel:${company.phone_number}` : undefined}
                                           onClick={(e) => {
                                               if (!company.phone_number) e.preventDefault();
                                           }}
                                           aria-disabled={!company.phone_number}
                                           style={{ opacity: company.phone_number ? 1 : 0.5, pointerEvents: company.phone_number ? 'auto' : 'none' }}
                                           >
                                            {formatPhoneNumber(company.phone_number) || 'Not Available'}
                                        </a>
                                        <span className="vendor__label">Phone</span>
                                    </div>
                                ) : (
                                    <div className="row">
                                        <button
                                            className="button button-black vendor__button_request_consultation"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setShowRequestConsultationModal(true);
                                            }}
                                            type="button"
                                            disabled={!company}
                                        >
                                            Request Legal Help
                                        </button>
                                    </div>
                                )
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Modal Rendering (only if showRequestConsultationModal is true and company data exists) */}
            {showRequestConsultationModal && company && (
                <RequestConsultationModal
                    show={showRequestConsultationModal}
                    recipient={company} // Pass the company data to the modal
                    recipientType="legalcompany" // Specify the recipient type
                    onClose={() => setShowRequestConsultationModal(false)} // Handler to close the modal
                />
            )}
        </>
    );
};

export default LawCompanyWithImage;
